import { DeleteOutlined, EyeOutlined, LeftOutlined, LoadingOutlined, PictureOutlined, PlusCircleOutlined, RightOutlined } from '@ant-design/icons'
import { Image, Tooltip, message } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import Utils from '@utils/utils'
import { UPLOAD_SOURCE } from '@utils/CONST'
import styles from './index.module.scss'

const MAX_UPLOAD_COUNT = 5
const ACCEPT_IMAGE_MIME_TYPES = ["image/jpeg", "image/png", "image/webp", "image/avif"]
const UNSUPPORTED_FILE_MESSAGE = "您只能上传JPG/PNG/WEBP/AVIF格式的文件"

const DetailImageUploader = ({ value, onChange }) => {
    const [innerImageList, setInnerImageList] = useState([])
    const [pendingUploadList, setPendingUploadList] = useState([])
    const [mainImageId, setMainImageId] = useState()
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewImage, setPreviewImage] = useState('')
    const [dragging, setDragging] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const pendingTokenRef = useRef(0)
    const orderRef = useRef(0)
    const latestImageListRef = useRef([])
    const imageListRef = useRef(null)

    const imageList = value !== undefined ? value : innerImageList
    useEffect(() => {
        latestImageListRef.current = imageList
    }, [imageList])
    const setImageList = (updater) => {
        const currentValue = latestImageListRef.current
        const nextValue = typeof updater === 'function' ? updater(currentValue) : updater
        latestImageListRef.current = nextValue
        if (value === undefined) {
            setInnerImageList(nextValue)
        }
        onChange?.(nextValue)
    }
    const totalCount = imageList.length + pendingUploadList.length
    const uploadDisabled = totalCount >= MAX_UPLOAD_COUNT || uploading
    const currentMainImageId = useMemo(() => {
        const mainItem = imageList.find(item => item.main)
        if (mainItem?.id) return mainItem.id
        if (mainImageId && imageList.find(item => item.id === mainImageId)) return mainImageId
        return imageList[0]?.id
    }, [imageList, mainImageId])

    useEffect(() => {
        if (!imageList.length || imageList.some(item => item.main)) return
        setImageList(prev => prev.map((item, index) => ({
            ...item,
            main: index === 0,
        })))
    }, [imageList])

    useEffect(() => {
        return () => {
            pendingUploadList.forEach((item) => {
                if (item?.previewUrl) {
                    URL.revokeObjectURL(item.previewUrl)
                }
            })
        }
    }, [pendingUploadList])

    const updateScrollState = () => {
        const el = imageListRef.current
        if (!el) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }
        const maxScrollLeft = el.scrollWidth - el.clientWidth
        setCanScrollLeft(el.scrollLeft > 2)
        setCanScrollRight(maxScrollLeft - el.scrollLeft > 2)
    }

    useEffect(() => {
        updateScrollState()
        const el = imageListRef.current
        if (!el) return
        const onScroll = () => updateScrollState()
        el.addEventListener('scroll', onScroll)
        window.addEventListener('resize', updateScrollState)
        return () => {
            el.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [imageList.length, pendingUploadList.length, totalCount])

    const normalizeFiles = (files = []) => {
        const restCount = MAX_UPLOAD_COUNT - imageList.length - pendingUploadList.length
        if (restCount <= 0) {
            message.warning(`最多上传${MAX_UPLOAD_COUNT}张图片`)
            return []
        }
        if (files.length > restCount) {
            message.warning(`最多上传${MAX_UPLOAD_COUNT}张图片，已自动忽略超出部分`)
        }
        return [...files].slice(0, restCount)
    }

    const getUploadErrorMessage = (reasonList = []) => {
        const uniqueReasons = [...new Set((reasonList || []).filter(Boolean))]
        if (!uniqueReasons.length) return '图片不符合要求'
        return uniqueReasons.join('，')
    } 

    const createPendingId = (file) => {
        pendingTokenRef.current += 1
        return `${file?.uid || file?.name || 'pending'}-${pendingTokenRef.current}`
    }

    const createOrder = () => {
        orderRef.current += 1
        return orderRef.current
    }

    const appendPendingItems = (files = []) => {
        const items = files.map((file) => ({
            id: createPendingId(file),
            name: file?.name || '待上传图片',
            previewUrl: URL.createObjectURL(file),
            order: createOrder(),
        }))
        setPendingUploadList((prev) => [...prev, ...items])
        return items
    }

    const removePendingItem = (pendingId) => {
        setPendingUploadList((prev) => {
            const current = prev.find((item) => item.id === pendingId)
            if (current?.previewUrl) {
                URL.revokeObjectURL(current.previewUrl)
            }
            return prev.filter((item) => item.id !== pendingId)
        })
    }

    const uploadSingleFile = async (file) => {
        await Utils.validateImageFile(file, true)
        const resizeRes = await Utils.resizeImageFileMaxByAliyunBy(file, 4096)
        if (!resizeRes || !resizeRes.success) throw new Error(resizeRes?.message || '图片处理失败，请重试')

        const legalRes = await Utils.checkImageIslegal(resizeRes.file, 0, UPLOAD_SOURCE.BATCHIMAGES)
        if (!legalRes) throw new Error('图片审核失败，请重试')
        if (!legalRes.isLegal) throw new Error('图片未通过审核，请更换图片后重试')

        return {
            id: resizeRes.file.uid || `${Date.now()}_${Math.random().toString(36).slice(2)}`,
            url: legalRes.data,
            width: resizeRes.width,
            height: resizeRes.height,
            name: file.name,
        }
    }

    const handleFiles = async (files = []) => {
        const normalizedFiles = normalizeFiles(files)
        if (normalizedFiles.length === 0) return

        setUploading(true)
        const pendingItems = appendPendingItems(normalizedFiles)
        const errorReasonList = []
        try {
            for (let index = 0; index < normalizedFiles.length; index++) {
                const file = normalizedFiles[index]
                const pendingItem = pendingItems[index]
                const pendingId = pendingItem?.id
                try {
                    const uploaded = await uploadSingleFile(file)
                    if (uploaded) {
                        const nextMainId = currentMainImageId || uploaded.id
                        setImageList((prev) => {
                            const hasMain = prev.some(item => item.main) || !!currentMainImageId
                            return [...prev, { ...uploaded, order: pendingItem?.order || createOrder(), main: !hasMain }]
                        })
                        if (nextMainId) setMainImageId((prev) => prev || nextMainId)
                    }
                } catch (err) {
                    if (err?.message) errorReasonList.push(err.message)
                } finally {
                    if (pendingId) removePendingItem(pendingId)
                }
            }
        } finally {
            setUploading(false)
            if (errorReasonList.length > 0) {
                message.error(getUploadErrorMessage(errorReasonList), 5)
            }
        }
    }

    const triggerUpload = async () => {
        if (uploadDisabled) return
        try {
            const files = await Utils.openFile2(true)
            await handleFiles(files)
        } catch (err) {
            if (err?.message !== 'cancel') {
                message.warning(err.message || '上传失败，请重试')
            }
        }
    }

    const handleDrop = async (event) => {
        event.preventDefault()
        event.stopPropagation()
        setDragging(false)
        if (uploadDisabled) return
        const droppedFiles = [...(event.dataTransfer?.files || [])]
        const files = droppedFiles.filter((file) => ACCEPT_IMAGE_MIME_TYPES.includes((file?.type || '').toLowerCase()))
        const initialErrorReasons = droppedFiles.length > files.length ? [UNSUPPORTED_FILE_MESSAGE] : []
        if (!files.length) {
            if (initialErrorReasons.length > 0) {
                message.error(getUploadErrorMessage(initialErrorReasons), 5)
            }
            return
        }
        if (initialErrorReasons.length > 0) {
            message.error(getUploadErrorMessage(initialErrorReasons), 5)
        }
        await handleFiles(files)
    }

    const handleDelete = (targetId) => {
        setImageList(prev => {
            const next = prev.filter(item => item.id !== targetId)
            if (targetId === currentMainImageId) {
                setMainImageId(next[0]?.id)
                return next.map((item, index) => ({ ...item, main: index === 0 }))
            }
            return next
        })
    }

    const uploadButton = (
        <button
            type="button"
            className={`${styles.UploadButton} ${uploadDisabled ? styles.UploadButtonDisabled : ''}`}
            onClick={(event) => {
                event.stopPropagation()
                triggerUpload()
            }}
            disabled={uploadDisabled}
        >
            <PlusCircleOutlined />
            <span>点击 / 拖拽上传详情页图片</span>
        </button>
    )

    const displayList = useMemo(() => {
        return [
            ...pendingUploadList.map((item) => ({ ...item, _status: 'pending' })),
            ...imageList.map((item) => ({ ...item, _status: 'done' })),
        ].sort((a, b) => (a.order || 0) - (b.order || 0))
    }, [pendingUploadList, imageList])

    const scrollImageList = (direction) => {
        const el = imageListRef.current
        if (!el) return
        el.scrollBy({
            left: direction * 136,
            behavior: 'smooth',
        })
    }

    return (
        <>
            <div className={styles.DetailImageUploader}>
                {totalCount === 0 ? (
                    <div
                        className={`${styles.EmptyUploadBox} ${dragging ? styles.EmptyUploadBoxDragging : ''}`}
                        onDragOver={(event) => {
                            event.preventDefault()
                            if (!uploadDisabled) setDragging(true)
                        }}
                        onDragLeave={(event) => {
                            event.preventDefault()
                            setDragging(false)
                        }}
                        onDrop={handleDrop}
                    >
                        <div className={styles.EmptyUploadContent}>
                            {uploadButton}
                            <div className={styles.EmptyUploadTip}>
                                请上传1-5张模特或衣服图
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.UploadPanel}>
                        <div className={styles.UploadButtonWrap}>{uploadButton}</div>
                        <div
                            className={`${styles.ListPanel} ${dragging ? styles.ListPanelDragging : ''}`}
                            onDragOver={(event) => {
                                event.preventDefault()
                                if (!uploadDisabled) setDragging(true)
                            }}
                            onDragLeave={(event) => {
                                event.preventDefault()
                                setDragging(false)
                            }}
                            onDrop={handleDrop}
                        >
                            <div className={styles.ImageListWrap}>
                                {canScrollLeft && (
                                    <button
                                        type="button"
                                        className={`${styles.ScrollArrow} ${styles.ScrollArrowLeft}`}
                                        onClick={() => scrollImageList(-1)}
                                    >
                                        <LeftOutlined />
                                    </button>
                                )}
                                {canScrollRight && (
                                    <button
                                        type="button"
                                        className={`${styles.ScrollArrow} ${styles.ScrollArrowRight}`}
                                        onClick={() => scrollImageList(1)}
                                    >
                                        <RightOutlined />
                                    </button>
                                )}
                                <div ref={imageListRef} className={`${styles.ImageList} _beauty_scrollbar`}>
                                {displayList.map((item) => {
                                    if (item._status === 'pending') {
                                        return (
                                            <div key={item.id} className={`${styles.ImageCard} ${styles.PendingImgItem} ${styles.PendingImgItemUploading}`}>
                                                {item.previewUrl ? (
                                                    <img src={item.previewUrl} alt={item.name || ''} className={styles.Image} />
                                                ) : (
                                                    <div className={styles.PendingImgFallback}></div>
                                                )}
                                                <div className={styles.PendingMask}>
                                                    <LoadingOutlined className={styles.PendingIcon} />
                                                    <span className={styles.PendingText}>上传中</span>
                                                </div>
                                            </div>
                                        )
                                    }
                                    const isMain = item.id === currentMainImageId
                                    return (
                                        <div key={item.id} className={styles.ImageCard}>
                                            <img src={Utils.getImgThumbnail(item.url)} alt={item.name || ''} className={styles.Image} />
                                            <div className={`${styles.Mask} ${isMain ? styles.MaskMain : ''}`}>
                                                {!isMain && (
                                                    <Tooltip title="设为主图" placement="right">
                                                        <button
                                                            type="button"
                                                            className={styles.IconBtn}
                                                            onClick={() => {
                                                                setMainImageId(item.id)
                                                                setImageList(prev => prev.map(prevItem => ({
                                                                    ...prevItem,
                                                                    main: prevItem.id === item.id,
                                                                })))
                                                            }}
                                                        >
                                                            <PictureOutlined />
                                                        </button>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="查看大图" placement="right">
                                                    <button
                                                        type="button"
                                                        className={styles.IconBtn}
                                                        onClick={() => {
                                                            setPreviewImage(item.url)
                                                            setPreviewVisible(true)
                                                        }}
                                                    >
                                                        <EyeOutlined />
                                                    </button>
                                                </Tooltip>
                                                <Tooltip title="删除" placement="right">
                                                    <button type="button" className={styles.IconBtn} onClick={() => handleDelete(item.id)}>
                                                        <DeleteOutlined />
                                                    </button>
                                                </Tooltip>
                                            </div>
                                            {isMain && <div className={styles.MainTag}>主图</div>}
                                        </div>
                                    )
                                })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div style={{ display: 'none' }}>
                <Image
                    src={previewImage}
                    preview={{
                        visible: previewVisible,
                        src: previewImage,
                        onVisibleChange: (visible) => {
                            setPreviewVisible(visible)
                            if (!visible) setPreviewImage('')
                        },
                    }}
                />
            </div>
        </>
    )
}

export default DetailImageUploader
